import React, { useId, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dropdown,
  Option,
  Label,
  MessageBar,
  MessageBarBody,
  TableCellLayout,
  tokens,
  type TableColumnDefinition,
  type TableColumnId,
} from "@fluentui/react-components";
import {
  ArrowUpload16Regular,
  DeleteRegular,
  DocumentImageRegular,
  DocumentPdfRegular,
  DocumentRegular,
  DocumentTableRegular,
  DocumentWordRegular,
} from "@fluentui/react-icons";
import { DataService } from "../../services/dataService";
import type { IDokumentItem } from "../../models/IDokumentItem";
import type { IDataItem } from "../../models/IDataItem";
import { formatDate } from "../../utils/format";
import { useDokumenteStyles as useStyles } from "../../utils/styles";

const ACCEPTED_FILE_TYPES =
  "image/jpeg, image/jpg, image/png, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const getDbFileType = (fileType: string): string => {
  if (fileType.includes("application/pdf")) return ".pdf";
  if (
    fileType.includes(
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    )
  )
    return ".docx";
  if (
    fileType.includes(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    )
  )
    return ".xlsx";
  if (fileType.includes("image/jpeg")) return ".jpg";
  if (fileType.includes("image/png")) return ".png";
  if (fileType.includes("image/bmp")) return ".bmp";
  return "";
};

const getDocumentTypeIcon = (fileType?: string) => {
  const type = (fileType ?? "").toLowerCase().replace(/^\./, "");

  if (type.includes("pdf")) return <DocumentPdfRegular aria-label="PDF" />;
  if (["doc", "docx"].includes(type))
    return <DocumentWordRegular aria-label="Word" />;
  if (["xls", "xlsx", "csv"].includes(type))
    return <DocumentTableRegular aria-label="Tabelle" />;
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(type))
    return <DocumentImageRegular aria-label="Bild" />;

  return <DocumentRegular aria-label="Dokument" />;
};

interface DokumenteProps {
  projektId: number;
  dokumente: IDokumentItem[];
  /** Shows upload and delete controls. */
  editable?: boolean;
  uploadNotice?: React.ReactNode;
  onDokumenteUpdated?: () => Promise<void>;
}

const Dokumente: React.FC<DokumenteProps> = ({
  projektId,
  dokumente,
  editable = false,
  uploadNotice,
  onDokumenteUpdated,
}) => {
  const styles = useStyles();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageTimeout = useRef<number | undefined>(undefined);
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [kategorien, setKategorien] = useState([] as IDataItem[]);
  const [selectedKategorie, setSelectedKategorie] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    color: string;
  } | null>(null);

  React.useEffect(() => () => window.clearTimeout(messageTimeout.current), []);

  const showMessage = (text: string, color: string) => {
    window.clearTimeout(messageTimeout.current);
    setMessage({ text, color });
    messageTimeout.current = window.setTimeout(() => setMessage(null), 3000);
  };

  React.useEffect(() => {
    if (!dataService) return;
    dataService
      .getDokumentKategorien()
      .then(setKategorien)
      .catch(console.error);
  }, [dataService]);

  const downloadFile = async (item: IDokumentItem) => {
    if (!dataService) return;

    try {
      const blob = await dataService.downloadDocument(item.dokGuid);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.dokumentName.endsWith(item.dateiTyp)
        ? item.dokumentName
        : `${item.dokumentName}${item.dateiTyp}`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
      showMessage("Fehler beim Herunterladen des Dokuments.", "red");
    }
  };

  const deleteFile = async (item: IDokumentItem) => {
    if (!dataService || isBusy) return;

    setIsBusy(true);
    try {
      await dataService.deleteProjektDokument(item.dokGuid);
      await onDokumenteUpdated?.();
    } catch (error) {
      console.error("Delete failed:", error);
      showMessage("Fehler beim Löschen des Dokuments.", "red");
    } finally {
      setIsBusy(false);
    }
  };

  const uploadFile = async () => {
    if (!dataService || !projektId || !file || isBusy) return;

    setIsBusy(true);
    try {
      const result = await dataService.uploadProjektDokument(
        projektId,
        file.name,
        getDbFileType(file.type),
        file,
        selectedKategorie,
      );

      if (result !== "") {
        setFile(null);
        setSelectedKategorie("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        showMessage("Dokument erfolgreich hochgeladen.", "green");
        await onDokumenteUpdated?.();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      showMessage("Fehler beim Hochladen des Dokuments.", "red");
    } finally {
      setIsBusy(false);
    }
  };

  const getColumnStyle = (columnId: TableColumnId): React.CSSProperties =>
    columnId === "dateiTyp" ? { flex: "0 0 60px", minWidth: "60px" } : {};

  const columns: TableColumnDefinition<IDokumentItem>[] = [
    createTableColumn<IDokumentItem>({
      columnId: "dateiTyp",
      renderHeaderCell: () => (
        <TableCellLayout className={styles.iconCell}>
          <DocumentRegular aria-label="Typ" />
        </TableCellLayout>
      ),
      compare: (a, b) => (a.dateiTyp ?? "").localeCompare(b.dateiTyp ?? ""),
      renderCell: (item) => (
        <TableCellLayout className={styles.iconCell}>
          <span
            title={item.dateiTyp ?? "Dokument"}
            aria-label={item.dateiTyp ?? "Dokument"}
            style={{ display: "inline-flex" }}
          >
            {getDocumentTypeIcon(item.dateiTyp)}
          </span>
        </TableCellLayout>
      ),
    }),
    createTableColumn<IDokumentItem>({
      columnId: "dokumentName",
      renderHeaderCell: () => "Name",
      compare: (a, b) =>
        (a.dokumentName ?? "").localeCompare(b.dokumentName ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.dokumentName ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IDokumentItem>({
      columnId: "kategorie",
      renderHeaderCell: () => "Kategorie",
      compare: (a, b) => (a.kategorie ?? "").localeCompare(b.kategorie ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.kategorie ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IDokumentItem>({
      columnId: "created",
      renderHeaderCell: () => "Hochgeladen am",
      compare: (a, b) => {
        const aTime = a.created ? new Date(a.created).getTime() : 0;
        const bTime = b.created ? new Date(b.created).getTime() : 0;
        return aTime - bTime;
      },
      renderCell: (item) => (
        <TableCellLayout>
          {item.created ? formatDate(item.created) : ""}
        </TableCellLayout>
      ),
    }),
    createTableColumn<IDokumentItem>({
      columnId: "ersteller",
      renderHeaderCell: () => "Geändert von",
      compare: (a, b) => (a.ersteller ?? "").localeCompare(b.ersteller ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.ersteller ?? ""}</TableCellLayout>
      ),
    }),
    ...(editable
      ? [
          createTableColumn<IDokumentItem>({
            columnId: "delete",
            renderHeaderCell: () => (
              <TableCellLayout className={styles.deleteCell} />
            ),
            compare: () => 0,
            renderCell: (item) => (
              <TableCellLayout className={styles.deleteCell}>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DeleteRegular />}
                  disabled={isBusy}
                  aria-label={`Dokument ${item.dokumentName} löschen`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void deleteFile(item);
                  }}
                />
              </TableCellLayout>
            ),
          }),
        ]
      : []),
  ];

  return (
    <>
      {editable && uploadNotice && (
        <MessageBar intent="warning" layout="multiline">
          <MessageBarBody>
            {uploadNotice}
            <span style={{ marginLeft: "15px" }}>
              <Dropdown
                id="kategorie"
                value={
                  kategorien.find((k) => k.value === selectedKategorie)?.text ??
                  ""
                }
                onOptionSelect={(_e, option) =>
                  option && setSelectedKategorie(option.optionValue ?? "")
                }
                placeholder="Kategorie auswählen"
              >
                {kategorien.map((kategorie) => (
                  <Option
                    key={kategorie.value}
                    value={kategorie.value}
                    text={kategorie.text}
                  >
                    {kategorie.text}
                  </Option>
                ))}
              </Dropdown>
              <label
                htmlFor={fileInputId}
                className={styles.fileUploadLabel}
                style={{ marginLeft: "15px" }}
              >
                Datei auswählen
              </label>
              <input
                id={fileInputId}
                ref={fileInputRef}
                className={styles.hiddenFileInput}
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                accept={ACCEPTED_FILE_TYPES}
              />
              {file?.name && <Label>{file.name}</Label>}
              <Button
                style={{ marginLeft: "15px" }}
                appearance="primary"
                icon={<ArrowUpload16Regular />}
                disabled={!file || isBusy}
                onClick={() => {
                  void uploadFile();
                }}
              >
                Upload
              </Button>
            </span>
          </MessageBarBody>
        </MessageBar>
      )}
      <section className={styles.section}>
        <div className={styles.headerRow}>
          <h3 className={styles.sectionTitle}>Dokumente</h3>
        </div>
        {message && (
          <div className={styles.message} style={{ color: message.color }}>
            {message.text}
          </div>
        )}
        {editable && (
          <div className={styles.uploadRow}>
            <Dropdown
              id="kategorie"
              value={
                kategorien.find((k) => k.value === selectedKategorie)?.text ??
                ""
              }
              onOptionSelect={(_e, option) =>
                option && setSelectedKategorie(option.optionValue ?? "")
              }
              placeholder="Kategorie auswählen"
            >
              {kategorien.map((kategorie) => (
                <Option
                  key={kategorie.value}
                  value={kategorie.value}
                  text={kategorie.text}
                >
                  {kategorie.text}
                </Option>
              ))}
            </Dropdown>
            <label htmlFor={fileInputId} className={styles.fileUploadLabel}>
              Datei auswählen
            </label>
            <input
              id={fileInputId}
              ref={fileInputRef}
              className={styles.hiddenFileInput}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              accept={ACCEPTED_FILE_TYPES}
            />
            {file?.name && <Label>{file.name}</Label>}
            <Button
              style={{ marginLeft: "15px" }}
              appearance="primary"
              icon={<ArrowUpload16Regular />}
              disabled={!file || isBusy}
              onClick={() => {
                void uploadFile();
              }}
            >
              Upload
            </Button>
          </div>
        )}
        <div className={styles.grid}>
          <DataGrid
            items={dokumente}
            columns={columns}
            sortable
            size="medium"
            aria-label="Dokumente"
            focusMode="composite"
            style={{ width: "100%" }}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell, columnId }) => (
                  <DataGridHeaderCell
                    className="dataGridHeaderCell"
                    style={{
                      ...getColumnStyle(columnId),
                      backgroundColor: tokens.colorNeutralBackground3,
                      color: tokens.colorNeutralForeground1,
                    }}
                  >
                    {renderHeaderCell()}
                  </DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<IDokumentItem>>
              {({ item, rowId }) => (
                <DataGridRow
                  key={rowId}
                  style={{
                    backgroundColor:
                      Number(rowId) % 2 === 1
                        ? tokens.colorNeutralBackground2
                        : "transparent",
                    color: tokens.colorNeutralForeground1,
                  }}
                >
                  {(column) => (
                    <DataGridCell
                      className="dataGridCell"
                      style={{
                        ...getColumnStyle(column.columnId),
                        color: tokens.colorNeutralForeground1,
                      }}
                      onClick={() => {
                        void downloadFile(item);
                      }}
                    >
                      {column.renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </div>
      </section>
    </>
  );
};

export default Dokumente;
